import { useState, type ReactNode } from "react";
import { Icon } from "./Icon";
import { Btn } from "./kit";
import { PLANS_LIST } from "../data/mock";
import { usePlans, type BillingMode, type ChangePlanResult } from "../lib/admin";

/* God Console action modals (single + bulk). Every destructive action carries a
   required reason and an audit note — the prototype's trust model. */

export function Modal({
  title, icon, tone, onClose, children, footer, wide,
}: {
  title: string; icon?: string; tone?: string; onClose: () => void;
  children: ReactNode; footer?: ReactNode; wide?: boolean;
}) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal ${tone || ""} ${wide ? "wide" : ""}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal-h">
          {icon && <span className={`modal-ic ${tone || ""}`}><Icon name={icon} size={17} /></span>}
          <span className="modal-t">{title}</span>
          <button className="icon-mini" onClick={onClose}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-b">{children}</div>
        {footer && <div className="modal-f">{footer}</div>}
      </div>
    </div>
  );
}

function MField({ label, hint, children }: { label: ReactNode; hint?: string; children: ReactNode }) {
  return (
    <div className="mfield">
      <label className="mfield-l">{label}{hint && <span className="mfield-h">{hint}</span>}</label>
      {children}
    </div>
  );
}

function AuditNote({ children }: { children?: ReactNode }) {
  return (
    <div className="audit-note">
      <Icon name="shield-alert" size={13} />
      {children || "Logged to the immutable audit trail with your operator ID."}
    </div>
  );
}

export function GrantModal({ target, onClose, onConfirm }: { target: string; onClose: () => void; onConfirm?: (amount: number, reason: string) => void }) {
  const [amt, setAmt] = useState(1000);
  const [reason, setReason] = useState("");
  return (
    <Modal title="Grant credits" icon="zap" tone="live" onClose={onClose}
      footer={<><Btn variant="ghost" size="sm" onClick={onClose}>Cancel</Btn><Btn variant="live" size="sm" icon="check" onClick={() => { onConfirm?.(amt, reason); onClose(); }}>Grant {amt.toLocaleString()}</Btn></>}>
      <MField label="Recipient"><div className="mtarget"><Icon name="user-round" size={14} />{target}</div></MField>
      <MField label="Amount (credits)">
        <div className="chip-row">
          {[500, 1000, 5000, 10000].map((a) => (
            <button key={a} className={`amt-chip ${amt === a ? "on" : ""}`} onClick={() => setAmt(a)}>{a.toLocaleString()}</button>
          ))}
        </div>
      </MField>
      <MField label="Reason" hint="required"><input className="ginput" placeholder="e.g. support comp for outage" value={reason} onChange={(e) => setReason(e.target.value)} /></MField>
      <AuditNote />
    </Modal>
  );
}

export function PlanModal({ target, current, preset, presetMode, onClose, submit }: {
  target: string; current: string; preset?: string; presetMode?: BillingMode;
  onClose: () => void;
  submit?: (args: { plan: string; reason: string; mode: BillingMode; netTermsDays: number }) => Promise<ChangePlanResult>;
}) {
  const [plan, setPlan] = useState(preset || current);
  const [reason, setReason] = useState("");
  const [mode, setMode] = useState<BillingMode>(presetMode || "charge");
  // Live Autumn prices, keyed by plan id (e.g. "pro"), overlaid on the static
  // rows. Falls back to the placeholder price until loaded / when not in Autumn.
  const livePlans = usePlans();
  const livePrice = (name: string): string | undefined => {
    const match = livePlans?.plans.find((p) => p.id.toLowerCase() === name.toLowerCase());
    return match?.display ?? (match?.amount != null ? `$${match.amount}${match.interval ? ` / ${match.interval}` : ""}` : undefined);
  };
  const [netTermsDays, setNetTermsDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);

  const isPaid = plan === "Pro" || plan === "Team";
  const confirmLabel = !isPaid ? `Move to ${plan}` : mode === "comp" ? `Comp ${plan}` : mode === "invoice" ? `Activate + invoice` : `Move to ${plan}`;

  const confirm = async () => {
    if (!submit) { onClose(); return; }
    setBusy(true);
    setCheckoutUrl(null);
    const res = await submit({ plan, reason, mode: isPaid ? mode : "charge", netTermsDays });
    setBusy(false);
    if (res.pending && res.checkoutUrl) { setCheckoutUrl(res.checkoutUrl); return; } // keep open to copy the link
    if (res.ok) onClose();
  };

  return (
    <Modal title="Change plan" icon="arrow-left-right" tone="violet" onClose={onClose}
      footer={<><Btn variant="ghost" size="sm" onClick={onClose}>Close</Btn><Btn variant="live" size="sm" icon="check" disabled={busy} onClick={confirm}>{busy ? "Working…" : confirmLabel}</Btn></>}>
      <MField label="Account"><div className="mtarget"><Icon name="user-round" size={14} />{target}</div></MField>
      <MField label="New plan">
        <div className="radio-list">
          {PLANS_LIST.map((p) => (
            <button key={p.name} className={`radio-row ${plan === p.name ? "on" : ""}`} onClick={() => setPlan(p.name)}>
              <span className="radio-dot" />
              <span className="radio-main"><b>{p.name}</b><small>{p.credits}</small></span>
              <span className="radio-price">{livePrice(p.name) ?? p.price}</span>
              {p.name === current && <span className="radio-cur">current</span>}
            </button>
          ))}
        </div>
      </MField>
      {isPaid && (
        <MField label="Billing">
          <div className="seg g">
            {([["charge", "Charge card"], ["comp", "Comp (free)"], ["invoice", "Invoice"]] as const).map(([v, l]) => (
              <button key={v} aria-pressed={mode === v} onClick={() => setMode(v)}>{l}</button>
            ))}
          </div>
          {mode === "charge" && <span className="muted-sm" style={{ display: "block", marginTop: 6 }}>Charges the card on file. No card → a checkout link is generated to send.</span>}
          {mode === "comp" && <span className="muted-sm" style={{ display: "block", marginTop: 6 }}>Grants full {plan} entitlements free — no charge, active immediately.</span>}
          {mode === "invoice" && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
              <span className="muted-sm">Net terms</span>
              <input className="ginput" type="number" style={{ width: 72 }} value={netTermsDays} onChange={(e) => setNetTermsDays(Number(e.target.value) || 30)} />
              <span className="muted-sm">days · active now, pay by bank/PO</span>
            </div>
          )}
        </MField>
      )}
      <MField label="Reason" hint="required"><input className="ginput" placeholder="e.g. enterprise pilot — net 30" value={reason} onChange={(e) => setReason(e.target.value)} /></MField>
      {checkoutUrl && (
        <MField label="Checkout link">
          <div className="modal-warn"><Icon name="link" size={15} /><span>No card on file — <b>{plan} isn’t active yet</b>. Send the customer this link to complete payment:</span></div>
          <input className="ginput" readOnly value={checkoutUrl} onFocus={(e) => e.target.select()} style={{ marginTop: 8 }} />
          <Btn variant="ghost" size="sm" icon="copy" onClick={() => navigator.clipboard?.writeText(checkoutUrl)}>Copy link</Btn>
        </MField>
      )}
      <AuditNote />
    </Modal>
  );
}

export function ImpersonateModal({ target, onClose, onConfirm }: { target: string; onClose: () => void; onConfirm: () => void }) {
  const [reason, setReason] = useState("");
  return (
    <Modal title="Impersonate user" icon="venetian-mask" tone="danger" onClose={onClose}
      footer={<><Btn variant="ghost" size="sm" onClick={onClose}>Cancel</Btn><Btn variant="danger" size="sm" icon="venetian-mask" onClick={onConfirm}>Start session</Btn></>}>
      <div className="modal-warn"><Icon name="alert-triangle" size={15} /><span>You’ll see Orbit exactly as <b>{target}</b> does. This is a <b>read &amp; write</b> session — actions you take affect their real account.</span></div>
      <MField label="Reason" hint="required"><input className="ginput" placeholder="e.g. reproduce stuck lesson" value={reason} onChange={(e) => setReason(e.target.value)} /></MField>
      <AuditNote>Impersonation is logged with start/end timestamps and every action taken.</AuditNote>
    </Modal>
  );
}

export function SuspendModal({ target, onClose, onConfirm }: { target: string; onClose: () => void; onConfirm?: (reason: string) => void }) {
  const [reason, setReason] = useState("");
  return (
    <Modal title="Suspend user" icon="pause" tone="danger" onClose={onClose}
      footer={<><Btn variant="ghost" size="sm" onClick={onClose}>Cancel</Btn><Btn variant="danger" size="sm" icon="pause" onClick={() => { onConfirm?.(reason); onClose(); }}>Suspend</Btn></>}>
      <div className="modal-warn"><Icon name="alert-triangle" size={15} /><span>{target} will be signed out and blocked from all access until reinstated.</span></div>
      <MField label="Reason" hint="required"><input className="ginput" placeholder="e.g. abuse report #182" value={reason} onChange={(e) => setReason(e.target.value)} /></MField>
      <AuditNote />
    </Modal>
  );
}

export function GdprModal({ target, onClose, onConfirm }: { target: string; onClose: () => void; onConfirm?: () => void }) {
  const [val, setVal] = useState("");
  return (
    <Modal title="Delete all data (GDPR)" icon="trash-2" tone="danger" onClose={onClose}
      footer={<><Btn variant="ghost" size="sm" onClick={onClose}>Cancel</Btn><Btn variant="danger" size="sm" icon="trash-2" style={val === target ? {} : { opacity: 0.5, pointerEvents: "none" }} onClick={() => { onConfirm?.(); onClose(); }}>Permanently delete</Btn></>}>
      <div className="modal-warn crit"><Icon name="alert-octagon" size={15} /><span>This erases the account, memory, history and lesson state. <b>Irreversible.</b></span></div>
      <MField label={`Type "${target}" to confirm`}><input className="ginput" placeholder={target} value={val} onChange={(e) => setVal(e.target.value)} /></MField>
      <AuditNote />
    </Modal>
  );
}

export function InviteModal({ org, onClose }: { org: string; onClose: () => void }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("Member");
  return (
    <Modal title="Invite member" icon="user-plus" tone="live" onClose={onClose}
      footer={<><Btn variant="ghost" size="sm" onClick={onClose}>Cancel</Btn><Btn variant="live" size="sm" icon="send">Send invite</Btn></>}>
      <MField label="Organization"><div className="mtarget"><Icon name="building-2" size={14} />{org}</div></MField>
      <MField label="Email" hint="required"><input className="ginput" placeholder="name@company.com" value={email} onChange={(e) => setEmail(e.target.value)} /></MField>
      <MField label="Role">
        <div className="radio-list tight">
          {["Member", "Admin", "Owner"].map((r) => (
            <button key={r} className={`radio-row ${role === r ? "on" : ""}`} onClick={() => setRole(r)}><span className="radio-dot" /><span className="radio-main"><b>{r}</b></span></button>
          ))}
        </div>
      </MField>
      <AuditNote>An invite email is sent via WorkOS and logged.</AuditNote>
    </Modal>
  );
}

export function ConfirmModal({ title, icon, tone, desc, confirmLabel, onClose, onConfirm }: {
  title: string; icon: string; tone?: string; desc: string; confirmLabel: string; onClose: () => void; onConfirm?: () => void;
}) {
  return (
    <Modal title={title} icon={icon} tone={tone} onClose={onClose}
      footer={<><Btn variant="ghost" size="sm" onClick={onClose}>Cancel</Btn><Btn variant={tone === "danger" ? "danger" : "live"} size="sm" icon="check" onClick={() => { onConfirm?.(); onClose(); }}>{confirmLabel}</Btn></>}>
      <p className="modal-desc">{desc}</p>
      <AuditNote />
    </Modal>
  );
}
